import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { 
  Phone, Plus, Trash2, Star, Loader2, Save, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import FlagIcon from '../../components/FlagIcon';

const API = process.env.REACT_APP_BACKEND_URL;

// Common country flags and codes for quick add
const COMMON_COUNTRIES = [
  { code: '+48', country: 'PL', flag: '🇵🇱', name: 'Polska' },
  { code: '+49', country: 'DE', flag: '🇩🇪', name: 'Niemcy' },
  { code: '+380', country: 'UA', flag: '🇺🇦', name: 'Ukraina' },
  { code: '+44', country: 'GB', flag: '🇬🇧', name: 'Wielka Brytania' },
  { code: '+1', country: 'US', flag: '🇺🇸', name: 'USA' },
  { code: '+33', country: 'FR', flag: '🇫🇷', name: 'Francja' },
  { code: '+39', country: 'IT', flag: '🇮🇹', name: 'Włochy' },
  { code: '+34', country: 'ES', flag: '🇪🇸', name: 'Hiszpania' },
  { code: '+31', country: 'NL', flag: '🇳🇱', name: 'Holandia' },
  { code: '+32', country: 'BE', flag: '🇧🇪', name: 'Belgia' },
  { code: '+43', country: 'AT', flag: '🇦🇹', name: 'Austria' },
  { code: '+41', country: 'CH', flag: '🇨🇭', name: 'Szwajcaria' },
  { code: '+420', country: 'CZ', flag: '🇨🇿', name: 'Czechy' },
  { code: '+421', country: 'SK', flag: '🇸🇰', name: 'Słowacja' },
  { code: '+370', country: 'LT', flag: '🇱🇹', name: 'Litwa' },
  { code: '+371', country: 'LV', flag: '🇱🇻', name: 'Łotwa' },
  { code: '+372', country: 'EE', flag: '🇪🇪', name: 'Estonia' },
  { code: '+375', country: 'BY', flag: '🇧🇾', name: 'Białoruś' },
  { code: '+7', country: 'RU', flag: '🇷🇺', name: 'Rosja' },
  { code: '+46', country: 'SE', flag: '🇸🇪', name: 'Szwecja' },
  { code: '+47', country: 'NO', flag: '🇳🇴', name: 'Norwegia' },
  { code: '+45', country: 'DK', flag: '🇩🇰', name: 'Dania' },
  { code: '+36', country: 'HU', flag: '🇭🇺', name: 'Węgry' },
  { code: '+40', country: 'RO', flag: '🇷🇴', name: 'Rumunia' },
  { code: '+359', country: 'BG', flag: '🇧🇬', name: 'Bułgaria' },
  { code: '+385', country: 'HR', flag: '🇭🇷', name: 'Chorwacja' },
  { code: '+386', country: 'SI', flag: '🇸🇮', name: 'Słowenia' },
  { code: '+381', country: 'RS', flag: '🇷🇸', name: 'Serbia' },
  { code: '+30', country: 'GR', flag: '🇬🇷', name: 'Grecja' },
  { code: '+351', country: 'PT', flag: '🇵🇹', name: 'Portugalia' },
  { code: '+353', country: 'IE', flag: '🇮🇪', name: 'Irlandia' },
  { code: '+358', country: 'FI', flag: '🇫🇮', name: 'Finlandia' },
  { code: '+354', country: 'IS', flag: '🇮🇸', name: 'Islandia' },
];

const AdminPhonePrefixes = () => {
  const { token } = useAdminAuth();
  const [prefixes, setPrefixes] = useState([]);
  const [defaultPrefix, setDefaultPrefix] = useState('+48');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newPrefix, setNewPrefix] = useState({ code: '', country: '', flag: '', name: '' });

  useEffect(() => {
    fetchPrefixes();
  }, []);

  const fetchPrefixes = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/admin/phone-prefixes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPrefixes(res.data.prefixes || []);
      setDefaultPrefix(res.data.defaultPrefix || '+48');
    } catch (error) {
      console.error('Failed to fetch prefixes:', error);
      toast.error('Nie udało się pobrać prefiksów');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPrefix = async () => {
    if (!newPrefix.code || !newPrefix.country || !newPrefix.flag || !newPrefix.name) {
      toast.error('Wypełnij wszystkie pola');
      return;
    }

    // Ensure code starts with +
    const code = newPrefix.code.startsWith('+') ? newPrefix.code : `+${newPrefix.code}`;

    try {
      setSaving(true);
      await axios.post(`${API}/api/admin/phone-prefixes`, {
        ...newPrefix,
        code,
        country: newPrefix.country.toUpperCase()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Prefiks dodany');
      setNewPrefix({ code: '', country: '', flag: '', name: '' });
      setShowAddForm(false);
      fetchPrefixes();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Nie udało się dodać prefiksu');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePrefix = async (code) => {
    if (!window.confirm(`Czy na pewno chcesz usunąć prefiks ${code}?`)) return;

    try {
      await axios.delete(`${API}/api/admin/phone-prefixes/${encodeURIComponent(code)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Prefiks usunięty');
      fetchPrefixes();
    } catch (error) {
      toast.error('Nie udało się usunąć prefiksu');
    }
  };

  const handleSetDefault = async (code) => {
    try {
      await axios.put(`${API}/api/admin/phone-prefixes/default`, {
        defaultPrefix: code
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDefaultPrefix(code);
      toast.success('Domyślny prefiks ustawiony');
    } catch (error) {
      toast.error('Nie udało się ustawić domyślnego prefiksu');
    }
  };

  const handleQuickAdd = (country) => {
    // Check if already exists
    if (prefixes.some(p => p.code === country.code)) {
      toast.error('Ten prefiks już istnieje');
      return;
    }
    setNewPrefix(country);
    setShowAddForm(true);
  };

  // Get countries not yet added
  const availableCountries = COMMON_COUNTRIES.filter(
    c => !prefixes.some(p => p.code === c.code)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Phone className="w-7 h-7 text-primary" />
            Prefiksy telefoniczne
          </h1>
          <p className="text-gray-400 mt-1">
            Zarządzaj listą prefiksów krajowych dostępnych przy rejestracji i w formularzach
          </p>
        </div>
        <Button 
          onClick={() => setShowAddForm(!showAddForm)} 
          className="bg-primary"
        >
          <Plus className="w-4 h-4 mr-2" />
          Dodaj prefiks
        </Button>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle className="text-white text-lg">Dodaj nowy prefiks</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Kod (np. +48)</label>
                <Input
                  value={newPrefix.code}
                  onChange={(e) => setNewPrefix({ ...newPrefix, code: e.target.value })}
                  placeholder="+48"
                  className="bg-black/30 border-white/10"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Kraj (np. PL)</label>
                <Input
                  value={newPrefix.country}
                  onChange={(e) => setNewPrefix({ ...newPrefix, country: e.target.value.toUpperCase() })}
                  placeholder="PL"
                  maxLength={3}
                  className="bg-black/30 border-white/10"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Flaga (emoji)</label>
                <Input
                  value={newPrefix.flag}
                  onChange={(e) => setNewPrefix({ ...newPrefix, flag: e.target.value })}
                  placeholder="🇵🇱"
                  className="bg-black/30 border-white/10"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Nazwa kraju</label>
                <Input
                  value={newPrefix.name}
                  onChange={(e) => setNewPrefix({ ...newPrefix, name: e.target.value })}
                  placeholder="Polska"
                  className="bg-black/30 border-white/10"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleAddPrefix} disabled={saving} className="bg-primary">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Zapisz
              </Button>
              <Button variant="outline" onClick={() => setShowAddForm(false)} className="border-white/20">
                Anuluj
              </Button>
            </div>

            {/* Quick add buttons */}
            {availableCountries.length > 0 && (
              <div className="pt-4 border-t border-white/10">
                <p className="text-sm text-gray-400 mb-3">Szybkie dodawanie (kliknij aby wypełnić formularz):</p>
                <div className="flex flex-wrap gap-2">
                  {availableCountries.slice(0, 12).map((country) => (
                    <button
                      key={country.code}
                      onClick={() => handleQuickAdd(country)}
                      className="px-3 py-1.5 bg-white/5 hover:bg-white/10 rounded-lg text-sm flex items-center gap-2 transition-colors"
                    >
                      <span><FlagIcon code={country.code} size={18} /></span>
                      <span className="text-gray-300">{country.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Default prefix info */}
      <Card className="bg-purple-500/10 border-purple-500/30">
        <CardContent className="py-4">
          <div className="flex items-center gap-3">
            <Star className="w-5 h-5 text-purple-400" />
            <span className="text-gray-300">
              Domyślny prefiks: <strong className="text-white">{defaultPrefix}</strong>
            </span>
            <span className="text-gray-500 text-sm">
              (używany jako wartość początkowa w formularzach)
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Prefixes list */}
      <Card className="bg-white/5 border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center justify-between">
            <span>Lista prefiksów ({prefixes.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {prefixes.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <AlertCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Brak prefiksów. Dodaj pierwszy prefiks powyżej.</p>
            </div>
          ) : (
            <div className="grid gap-2">
              {prefixes.map((prefix) => (
                <div
                  key={prefix.code}
                  className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                    prefix.code === defaultPrefix 
                      ? 'bg-purple-500/20 border border-purple-500/30' 
                      : 'bg-black/20 hover:bg-black/30'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <FlagIcon code={prefix.country} size={28} />
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium">{prefix.name}</span>
                        <span className="text-gray-500 text-sm">({prefix.country})</span>
                        {prefix.code === defaultPrefix && (
                          <span className="px-2 py-0.5 bg-purple-500/30 text-purple-300 text-xs rounded-full">
                            domyślny
                          </span>
                        )}
                      </div>
                      <span className="text-purple-400 font-mono">{prefix.code}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {prefix.code !== defaultPrefix && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSetDefault(prefix.code)}
                        className="text-gray-400 hover:text-purple-400"
                        title="Ustaw jako domyślny"
                      >
                        <Star className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeletePrefix(prefix.code)}
                      className="text-gray-400 hover:text-red-400"
                      title="Usuń"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPhonePrefixes;
